import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { NODE_REGISTRY, NodeCategory } from '../config/nodeRegistry';
import Tooltip from '@mui/material/Tooltip';
import { KnodsIcon } from './ui/KnodsIcon';
import { ModelTooltip } from './ModelTooltip';
import { useTutorial } from '../contexts/TutorialContext';
import { useIsMobile } from '../hooks/useIsMobile';
import logger from '../utils/logger';

// Figma sidebar icons - main icons have rounded rect background, bottom icons are just paths
// Main icons (Text, Image, Video) have default and active variants
const TextIcon = ({ active }: { active?: boolean }) => (
  <svg 
    className="sidebar-icon-main icon-text"
    data-active={active ? "true" : "false"}
    preserveAspectRatio="none" 
    width="100%" 
    height="100%" 
    overflow="visible" 
    style={{ display: 'block' }} 
    viewBox="0 0 60 60" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="Text BTN">
      <rect x="1" y="1" width="58" height="58" rx="13" fill={active ? "#FED402" : "var(--fill-0, #2B2B2B)"}/>
      <rect x="1" y="1" width="58" height="58" rx="13" stroke={active ? "#2B2B2B" : "currentColor"} strokeWidth="2"/>
      <path 
        id="Vector" 
        d="M24.7525 43.125H30.3637M30.3637 43.125H36.2346M30.3637 43.125V16.875M30.3637 16.875H21.2109C20.2401 16.875 19.4531 17.7145 19.4531 18.75V21.5074M30.3637 16.875H38.7891C39.7599 16.875 40.5469 17.7145 40.5469 18.75V22.2794" 
        stroke={active ? "#2B2B2B" : "currentColor"} 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

const ImageIconSidebar = ({ active }: { active?: boolean }) => (
  <svg 
    className="sidebar-icon-main icon-image"
    data-active={active ? "true" : "false"}
    preserveAspectRatio="none" 
    width="100%" 
    height="100%" 
    overflow="visible" 
    style={{ display: 'block' }} 
    viewBox="0 0 60 60" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="Image BTN">
      <rect x="1" y="1" width="58" height="58" rx="13" fill={active ? "#42C3D7" : "var(--fill-0, #2B2B2B)"}/>
      <rect x="1" y="1" width="58" height="58" rx="13" stroke={active ? "#2B2B2B" : "currentColor"} strokeWidth="2"/>
      <path 
        id="Icon" 
        d="M2.27988 50.3059C1.89668 50.7036 1.90844 51.3366 2.30615 51.7198C2.70386 52.103 3.33691 52.0913 3.72012 51.6936L3 50.9997L2.27988 50.3059ZM17.5732 35.8749L18.2795 35.167C18.0897 34.9775 17.8316 34.8723 17.5634 34.8749C17.2951 34.8776 17.0392 34.9879 16.8531 35.181L17.5732 35.8749ZM24.7558 43.0416L24.0494 43.7495C24.4398 44.139 25.0718 44.139 25.4621 43.7495L24.7558 43.0416ZM40.9165 26.9165L41.6204 26.2061C41.2296 25.819 40.5996 25.8201 40.2102 26.2086L40.9165 26.9165ZM58.2962 45.5437C58.6885 45.9324 59.3217 45.9294 59.7104 45.5371C60.0991 45.1448 60.0962 44.5116 59.7038 44.1229L59 44.8333L58.2962 45.5437ZM3 50.9997L3.72012 51.6936L18.2933 36.5687L17.5732 35.8749L16.8531 35.181L2.27988 50.3059L3 50.9997ZM17.5732 35.8749L16.8669 36.5828L24.0494 43.7495L24.7558 43.0416L25.4621 42.3337L18.2795 35.167L17.5732 35.8749ZM24.7558 43.0416L25.4621 43.7495L41.6229 27.6244L40.9165 26.9165L40.2102 26.2086L24.0494 42.3337L24.7558 43.0416ZM40.9165 26.9165L40.2127 27.6269L58.2962 45.5437L59 44.8333L59.7038 44.1229L41.6204 26.2061L40.9165 26.9165ZM21.1645 15.3748H20.1645C20.1645 17.7889 18.2025 19.7498 15.7776 19.7498V20.7498V21.7498C19.3029 21.7498 22.1645 18.8977 22.1645 15.3748H21.1645ZM15.7776 20.7498V19.7498C13.3527 19.7498 11.3907 17.7889 11.3907 15.3748H10.3907H9.39066C9.39066 18.8977 12.2523 21.7498 15.7776 21.7498V20.7498ZM10.3907 15.3748H11.3907C11.3907 12.9606 13.3527 10.9997 15.7776 10.9997V9.99973V8.99973C12.2523 8.99973 9.39066 11.8518 9.39066 15.3748H10.3907ZM15.7776 9.99973V10.9997C18.2025 10.9997 20.1645 12.9606 20.1645 15.3748H21.1645H22.1645C22.1645 11.8518 19.3029 8.99973 15.7776 8.99973V9.99973Z" 
        fill={active ? "#2B2B2B" : "currentColor"}
      />
    </g>
  </svg>
);

const VideoIcon = ({ active }: { active?: boolean }) => (
  <svg 
    className="sidebar-icon-main icon-video"
    data-active={active ? "true" : "false"}
    preserveAspectRatio="none" 
    width="100%" 
    height="100%" 
    overflow="visible" 
    style={{ display: 'block' }} 
    viewBox="0 0 60 60" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="Video BTN">
      <rect x="1" y="1" width="58" height="58" rx="13" fill={active ? "#FF4B42" : "var(--fill-0, #2B2B2B)"}/>
      <rect x="1" y="1" width="58" height="58" rx="13" stroke={active ? "#2B2B2B" : "currentColor"} strokeWidth="2"/>
      <path 
        id="Icon" 
        d="M1 15.9997L58.9833 15.9997M1 43.9997L58.9833 43.9997M29.9335 1.99972L29.9335 57.9997M43.9334 1.99972V15.9997M43.9334 43.9997V57.9997M15.9336 1.99972V15.9997M15.9336 43.9997V57.9997" 
        stroke={active ? "#2B2B2B" : "currentColor"} 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
    </g>
  </svg>
);
const AudioIcon = ({ active }: { active?: boolean }) => (
  <svg
    className="sidebar-icon-main icon-audio"
    data-active={active ? "true" : "false"}
    preserveAspectRatio="none"
    width="100%"
    height="100%"
    overflow="visible"
    style={{ display: 'block' }}
    viewBox="0 0 60 60"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="Audio BTN">
      <rect x="1" y="1" width="58" height="58" rx="13" fill={active ? "#10B981" : "var(--fill-0, #2B2B2B)"}/>
      <rect x="1" y="1" width="58" height="58" rx="13" stroke={active ? "#2B2B2B" : "currentColor"} strokeWidth="2"/>
      <path
        id="Icon"
        d="M14 26V34M22 20V40M30 13V47M38 20V40M46 26V34"
        stroke={active ? "#2B2B2B" : "currentColor"}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);
const UtilityIcon = ({ active }: { active?: boolean }) => (
  <svg
    className="sidebar-icon-main icon-utility"
    data-active={active ? "true" : "false"}
    preserveAspectRatio="none"
    width="100%"
    height="100%"
    overflow="visible"
    style={{ display: 'block' }}
    viewBox="0 0 60 60"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="Utility BTN">
      <rect x="1" y="1" width="58" height="58" rx="13" fill={active ? "#9CA3AF" : "var(--fill-0, #2B2B2B)"} />
      <rect x="1" y="1" width="58" height="58" rx="13" stroke={active ? "#2B2B2B" : "currentColor"} strokeWidth="2" />
      {/* Three small boxes representing input, prompt, output containers */}
      <rect x="14" y="14" width="14" height="14" rx="2" stroke={active ? "#2B2B2B" : "currentColor"} strokeWidth="2" fill="none" />
      <rect x="32" y="14" width="14" height="14" rx="2" stroke={active ? "#2B2B2B" : "currentColor"} strokeWidth="2" fill="none" />
      <rect x="23" y="32" width="14" height="14" rx="2" stroke={active ? "#2B2B2B" : "currentColor"} strokeWidth="2" fill="none" />
    </g>
  </svg>
);

// Bottom icons - just icon paths without backgrounds
const AssetsIcon = () => <img src="/icons/gallery-figma.svg" alt="Assets" className="sidebar-icon" />;
const IrisIcon = () => <img src="/icons/iris-butterfly.svg" alt="Iris" className="sidebar-icon iris-icon" />;

// MUI icons for node cards (keep these for internal use)
import TextFieldsIcon from '@mui/icons-material/TextFields';

import './NodePalette.css';

export type PaletteSection = 'text' | 'image' | 'video' | 'audio' | 'utility' | 'assets' | 'help' | 'settings';

/**
 * Sections that stay open until the user closes them explicitly (the section's
 * own close button or its sidebar toggle). The node lists are fire-and-forget
 * flyouts, but the Assets panel is a workspace: people place several images,
 * select, download - closing it on every canvas click or placement made that
 * impossible.
 */
export const STICKY_PALETTE_SECTIONS: ReadonlySet<PaletteSection> = new Set<PaletteSection>(['assets']);

export interface NodePaletteRef {
  /** Close a transient flyout. Leaves a sticky section (Assets) open. */
  closeSubmenu: () => void;
  isAssetsActive: () => boolean;
  /** Screen rect of the palette overlay (sidebar plus any open panel). */
  getOverlayRect: () => DOMRect | null;
  openCategory: (category: PaletteSection) => void;
}

interface NodePaletteProps {
  onNodeSelect: (nodeType: string) => void;
  onNodeDragStart: (event: React.DragEvent, nodeType: string) => void;
  onBeforeNavigateHome?: (force?: boolean) => Promise<void>;
  /** React node for assets panel content - rendered in a portal container */
  assetsPanel?: React.ReactNode;
  /** @deprecated no longer used — Iris is now a floating panel */
  helpPanel?: React.ReactNode;
  /** Called when the user clicks the Iris button */
  onIrisOpen?: () => void;
}

export const NodePalette = forwardRef<NodePaletteRef, NodePaletteProps>(({ onNodeSelect, onNodeDragStart, onBeforeNavigateHome, assetsPanel, helpPanel, onIrisOpen }, ref) => {
  const [activeCategory, setActiveCategory] = useState<PaletteSection | null>(null);
  const [assetsPanelContainer, setAssetsPanelContainer] = useState<HTMLDivElement | null>(null);
  const [helpPanelContainer, setHelpPanelContainer] = useState<HTMLDivElement | null>(null);
  const paletteRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { completeAction } = useTutorial();
  const isMobile = useIsMobile();

  // Mirror of activeCategory for the document listeners registered once below.
  const activeCategoryRef = useRef<PaletteSection | null>(null);
  activeCategoryRef.current = activeCategory;

  // Implicit close (canvas click, node drag, outside interaction). A sticky
  // section ignores it; only its close button or sidebar toggle closes it.
  const closeTransientSubmenu = () => {
    setActiveCategory(prev => (prev && STICKY_PALETTE_SECTIONS.has(prev) ? prev : null));
  };

  // Expose closeSubmenu method to parent components
  useImperativeHandle(ref, () => ({
    closeSubmenu: closeTransientSubmenu,
    isAssetsActive: () => activeCategory === 'assets',
    getOverlayRect: () => paletteRef.current?.getBoundingClientRect() ?? null,
    openCategory: (category: PaletteSection) => setActiveCategory(category)
  }));

  // Close submenu when clicking or dragging outside the palette
  useEffect(() => {
    const handleInteractionOutside = (event: MouseEvent | DragEvent) => {
      // Don't close if clicking inside the palette
      if (paletteRef.current && paletteRef.current.contains(event.target as Node)) {
        return;
      }
      // Don't close if clicking inside a lightbox (portaled to body)
      const lightbox = document.querySelector('.lightbox-overlay');
      if (lightbox && lightbox.contains(event.target as Node)) {
        return;
      }
      // The Assets panel stays open while the user works on the canvas.
      const current = activeCategoryRef.current;
      if (current && STICKY_PALETTE_SECTIONS.has(current)) {
        return;
      }
      setActiveCategory(null);
    };

    document.addEventListener('mousedown', handleInteractionOutside);
    document.addEventListener('dragstart', handleInteractionOutside);
    document.addEventListener('touchstart', handleInteractionOutside as EventListener);
    return () => {
      document.removeEventListener('mousedown', handleInteractionOutside);
      document.removeEventListener('dragstart', handleInteractionOutside);
      document.removeEventListener('touchstart', handleInteractionOutside as EventListener);
    };
  }, []);

  const handleCategoryClick = (category: PaletteSection) => {
    // Iris button opens the floating panel instead of the sidebar submenu
    if (category === 'help') {
      onIrisOpen?.();
      return;
    }
    const isOpening = activeCategory !== category;
    if (isOpening) {
      setActiveCategory(category);
      if (category === 'image') {
        completeAction('click', { target: '[data-tutorial="image-category"]' });
      }
    } else {
      setActiveCategory(null);
    }
  };

  const renderSidebarButton = (category: PaletteSection, icon: React.ReactNode, label: string, description?: string) => {
    const tutorialTag = category === 'image'
      ? 'image-category'
      : undefined;

    const tooltipTitle = description ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px' }}>
        <div style={{ fontWeight: 600, fontSize: '14px' }}>{label}</div>
        <div style={{ fontSize: '12px', opacity: 0.95, lineHeight: 1.4 }}>{description}</div>
      </div>
    ) : label;

    const buttonEl = (
      <button
        className={`sidebar-button category-${category} ${activeCategory === category ? 'active' : ''}`}
        onClick={() => handleCategoryClick(category)}
        data-tutorial={tutorialTag}
      >
        <span className='icon'>{icon}</span>
        <span className='label'>{label}</span>
      </button>
    );

    // Skip tooltip on mobile (tooltips don't work with touch)
    if (isMobile) return buttonEl;

    return (
    <Tooltip
      title={tooltipTitle}
      placement='right'
      arrow
      slotProps={{
        popper: {
          sx: { pointerEvents: 'none' }
        }
      }}
    >
      {buttonEl}
    </Tooltip>
    );
  };

  const renderNodeList = (category: NodeCategory) => {
    const nodes = Object.values(NODE_REGISTRY)
      .filter(node => node.category === category && !node.hidden)
      .sort((a, b) => (a.definition?.title || a.type).localeCompare(b.definition?.title || b.type));

    if (nodes.length === 0) {
      return <div className='empty-state'>No nodes available</div>;
    }

    return nodes.map(node => {
      const tutorialTag = node.camelCaseType === 'zImageTurbo'
        ? 'z-image-turbo-node'
        : node.category === 'image'
          ? 'image-node'
          : node.category === 'video'
            ? 'video-node'
            : (node.camelCaseType === 'startingPrompt' || node.camelCaseType === 'agentPrompt')
              ? 'prompt-node'
              : undefined;

      return (
      <ModelTooltip
        key={node.type}
        node={node}
        disabled={isMobile}
      >
        <div
          className={`node-card type-${category}`}
          data-tutorial={tutorialTag}
          draggable={!isMobile}
          onDragStart={isMobile ? undefined : (e) => onNodeDragStart(e, node.camelCaseType)}
          onClick={() => {
            onNodeSelect(node.camelCaseType);
            setActiveCategory(null);
          }}
        >
          <div className='node-card-header'>
            <div className='node-card-icon'>
              {node.logoPath ? (
                <img src={node.logoPath} alt={node.definition.title} className="model-logo" />
              ) : node.icon ? (
                React.createElement(node.icon, { style: { fontSize: 40 } })
              ) : (
                <TextFieldsIcon style={{ fontSize: 40 }} />
              )}
            </div>
            <div className='node-card-title'>{node.definition.title}</div>
          </div>
        </div>
      </ModelTooltip>
      );
    });
  };

  const renderSubmenuContent = () => {
    const closeButton = (
      <button 
        className="submenu-close-button" 
        onClick={() => setActiveCategory(null)}
        aria-label="Close submenu"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 6L18 18M6 18L18 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </button>
    );

    switch (activeCategory) {
      case 'text':
        return (
          <>
            <div className='submenu-header'>
              {closeButton}
              <h3>Text</h3>
            </div>
            <div className='submenu-content'>
              {renderNodeList('text')}
            </div>
          </>
        );
      case 'image':
        return (
          <>
            <div className='submenu-header'>
              {closeButton}
              <h3>Image</h3>
            </div>
            <div className='submenu-content'>
              {renderNodeList('image')}
            </div>
          </>
        );
      case 'video':
        return (
          <>
            <div className='submenu-header'>
              {closeButton}
              <h3>Video</h3>
            </div>
            <div className='submenu-content'>
              {renderNodeList('video')}
            </div>
          </>
        );
      case 'audio':
        return (
          <>
            <div className='submenu-header'>
              {closeButton}
              <h3>Audio</h3>
            </div>
            <div className='submenu-content'>
              {renderNodeList('audio')}
            </div>
          </>
        );
      case 'utility':
        return (
          <>
            <div className='submenu-header'>
              {closeButton}
              <h3>Utility</h3>
            </div>
            <div className='submenu-content'>
              {renderNodeList('utility')}
            </div>
          </>
        );
      case 'assets':
        return (
          <>
            <div className='submenu-header'>
              {closeButton}
              <h3>Assets</h3>
            </div>
            <div className='submenu-content assets-panel-container' ref={setAssetsPanelContainer}>
              {/* FilePanel will be portaled here when active */}
            </div>
          </>
        );
      case 'help':
        return (
          <>
            <div className='submenu-header'>
              {closeButton}
              <h3>Iris</h3>
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--color-text-secondary)', fontWeight: '400' }}>The Knod Whisperer.</p>
            </div>
            <div className='submenu-content help-panel-container' ref={setHelpPanelContainer}>
              {/* HelperChat will be portaled here when active */}
            </div>
          </>
        );
      case 'settings':
        return (
          <>
            <div className='submenu-header'>
              {closeButton}
              <h3>Settings</h3>
            </div>
            <div className='submenu-content'>
              <div className='node-card'>
                <div className='node-card-title'>Preferences</div>
                <div className='node-card-description'>Configure editor settings</div>
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  // Handle home navigation with save.
  //
  // The save still runs, and still forced — but nothing waits for it. Awaiting
  // it meant the logo did nothing at all for as long as the save took, and a
  // save is an auth round-trip plus an upsert of the entire node graph (plus a
  // blob->R2 upload if any node is holding one). The click looked dead, and
  // only then did the dashboard start loading.
  //
  // Dropping the await is safe: onSave snapshots nodes, edges and viewport
  // synchronously before its first await, so the in-flight save is unaffected
  // by this component unmounting, and src/services/pendingFlowSaves.ts keeps a
  // module-scope beforeunload guard up until it settles — a guard registered in
  // the editor would be torn down by this very navigation.
  const handleHomeClick = () => {
    onBeforeNavigateHome?.(true).catch((error) => {
      logger.error('[NodePalette] Save on navigate-home failed:', error);
    });
    navigate('/');
  };

  return (
    <div className='node-palette-container' ref={paletteRef} data-tutorial="node-palette">
      <div className='node-palette-sidebar'>
        <div className='sidebar-logo' onClick={handleHomeClick} onMouseEnter={closeTransientSubmenu} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleHomeClick()}>
          <KnodsIcon />
        </div>

        <div className='sidebar-section main-buttons'>
          {renderSidebarButton('text', <TextIcon active={activeCategory === 'text'} />, 'Text', 'Create and refine text with AI. Prompts, summaries, writing tasks and more.')}
          {renderSidebarButton('image', <ImageIconSidebar active={activeCategory === 'image'} />, 'Image', 'Generate images, edit and enhance visuals. Powerful image manipulation tools.')}
          {renderSidebarButton('video', <VideoIcon active={activeCategory === 'video'} />, 'Video', 'Create and edit video content. Generate video from text or images.')}
          {renderSidebarButton('audio', <AudioIcon active={activeCategory === 'audio'} />, 'Audio', 'Generate speech, sound effects, and music from text with ElevenLabs.')}
          {renderSidebarButton('utility', <UtilityIcon active={activeCategory === 'utility'} />, 'Utility', 'Container nodes for inputs, outputs, and document panels.')}
        </div>

        <div className='sidebar-section bottom'>
          {renderSidebarButton('assets', <AssetsIcon />, 'Assets', 'Upload, browse, and organize your media. Drag files in or import from your phone.')}
          {renderSidebarButton('help', <IrisIcon />, 'Iris', 'AI assistant ready to help. Get guidance and answers about building workflows.')}
          {/* Settings button hidden for now */}
        </div>
      </div>

      {activeCategory && (
        <div className={`node-palette-submenu category-${activeCategory}`}>
          {renderSubmenuContent()}
        </div>
      )}

      {/* Assets panel - always mounted for live updates, portaled to submenu when active */}
      {assetsPanel && (
        activeCategory === 'assets' && assetsPanelContainer
          ? ReactDOM.createPortal(assetsPanel, assetsPanelContainer)
          : <div className="assets-preload-container" style={{ display: 'none' }}>{assetsPanel}</div>
      )}

      {/* Help panel - portaled to submenu when active */}
      {helpPanel && activeCategory === 'help' && helpPanelContainer && (
        ReactDOM.createPortal(helpPanel, helpPanelContainer)
      )}
    </div>
  );
});

